import { observer } from "mobx-react-lite";
import { useState, type ReactNode } from "react";
import { AstrialLogo } from "../astrial-logo";
import { EngageLogo } from "../engage-logo";
import { Button } from "../../ui/button";
import { Card } from "../../ui/card";
import { ErrorState } from "../../ui/error-state";
import { Field } from "../../ui/field";
import { Form } from "../../ui/form";
import { StatusPill, type StatusTone } from "../../ui/status-pill";
import { TextInput } from "../../ui/text-input";
import { useAppStores } from "../app-context";
import { ApiRequestError } from "../../platform/api-client";
import type { MessageKey } from "../locale-store";
import { OperationalIcon } from "../../ui/operational-icon";
import { Select } from "../../ui/select";
import { themeCodes, type ThemeCode } from "../theme-store";
import { ProjectIdentity } from "../branding/project-identity";
import { ProjectInformation } from "../branding/project-information";
import { FundingAcknowledgement } from "../branding/funding-acknowledgement";
import { researchProjects } from "../branding/branding-config";
import { authenticate } from "./session-api";
import europeBackdrop from "../branding/assets/europe-research-backdrop.webp";
import responseArtwork from "../branding/assets/research-response.webp";
import logisticsArtwork from "../branding/assets/research-logistics.webp";

const platformFeatures = [
  { icon: "location", title: "brandingFeatureSituationTitle", detail: "brandingFeatureSituationDetail" },
  { icon: "users", title: "brandingFeatureCollaborationTitle", detail: "brandingFeatureCollaborationDetail" },
  { icon: "capability-resources", title: "brandingFeatureResourcesTitle", detail: "brandingFeatureResourcesDetail" },
  { icon: "requirements", title: "brandingFeatureDecisionsTitle", detail: "brandingFeatureDecisionsDetail" },
] as const;

/** Presentation-only C3I sign-in. Authentication and CSRF ordering stay unchanged. */
export const LoginView = observer(function LoginView(): ReactNode {
  const { api, locale, session, status, theme } = useAppStores();
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [identifierError, setIdentifierError] = useState<MessageKey | null>(null);
  const [passwordError, setPasswordError] = useState<MessageKey | null>(null);
  const [formError, setFormError] = useState<MessageKey | null>(null);

  async function submit(): Promise<void> {
    const missingIdentifier = loginIdentifier.trim().length === 0;
    const missingPassword = password.length === 0;
    setIdentifierError(missingIdentifier ? "loginIdentifierRequired" : null);
    setPasswordError(missingPassword ? "loginPasswordRequired" : null);
    if (missingIdentifier || missingPassword) {
      setFormError("loginErrorSummaryTitle");
      return;
    }

    setFormError(null);
    setSubmitting(true);
    try {
      const result = await authenticate({ loginIdentifier, password });
      // Order matters: publish the CSRF token before the bootstrap so any
      // follow-up mutation the shell issues is already armed.
      session.setCsrfToken(result.csrfToken);
      // The gate takes over once bootstrap flips to `loading`; this component
      // unmounts, so no state is set after this point.
      await session.bootstrap(api);
    } catch (error) {
      setFormError(
        error instanceof ApiRequestError && error.status === 401
          ? "loginInvalidCredentials"
          : "loginServiceError",
      );
      setSubmitting(false);
    }
  }

  const summaryVisible = formError !== null;

  // Reflect the existing form state without introducing a second status source.
  const sessionPhase: { tone: StatusTone; label: string } | null = submitting
    ? { label: locale.text("loginSubmitting"), tone: "info" }
    : formError !== null
      ? { label: locale.text("loginErrorSummaryTitle"), tone: "danger" }
      : null;

  const themeLabels = { "dark-blue": "theme_dark_blue", light: "theme_light", grey: "theme_grey", "high-contrast": "theme_high_contrast" } as const;
  return (
    <div className="login-page">
      <header className="c3i-login-toolbar">
        <div aria-label={locale.text("loginBrandLabel")} className="login-brand" role="img">
          <EngageLogo name={locale.text("appName")} subtitle={locale.text("shellEngageSubtitle")} />
        </div>
        <div className="c3i-login-preferences">
          <div aria-label={locale.text("languageLabel")} className="c3i-language" role="group">
            <Button aria-pressed={locale.code === "de"} onClick={() => void locale.setLocale("de")} variant={locale.code === "de" ? "primary" : "secondary"}>{locale.text("brandingLanguageDe")}</Button>
            <Button aria-pressed={locale.code === "en"} onClick={() => void locale.setLocale("en")} variant={locale.code === "en" ? "primary" : "secondary"}>{locale.text("brandingLanguageEn")}</Button>
          </div>
          <Select aria-label={locale.text("themeLabel")} onChange={(event) => { theme.setTheme(event.target.value as ThemeCode); }} value={theme.theme}>
            {themeCodes.map((code) => <option key={code} value={code}>{locale.text(themeLabels[code])}</option>)}
          </Select>
        </div>
      </header>
      <main className="c3i-login-main">
        <div className="c3i-login-hero">
          <div aria-hidden="true" className="c3i-login-scene">
            <img alt="" className="c3i-login-backdrop" height="1024" src={europeBackdrop} width="1536" />
          </div>
          <div aria-hidden="true" className="c3i-login-vignettes">
            <div className="c3i-login-vignette c3i-login-vignette--air"><img alt="" height="1024" src={europeBackdrop} width="1536" /></div>
            <div className="c3i-login-vignette c3i-login-vignette--ground"><img alt="" height="1024" src={europeBackdrop} width="1536" /></div>
            <div className="c3i-login-vignette c3i-login-vignette--terrain"><img alt="" height="1024" src={logisticsArtwork} width="1536" /></div>
          </div>
          <section aria-labelledby="login-welcome-title" className="c3i-login-identity">
            <p className="c3i-login-eyebrow">{locale.text("brandingResearchProjects")}</p>
            <div className="c3i-login-project-logos">
              {researchProjects.map((project) => <ProjectIdentity key={project.id} onDark={theme.theme === "dark-blue" || theme.theme === "high-contrast"} project={project} />)}
            </div>
            <h1 id="login-welcome-title"><span>{locale.text("brandingProductPrefix")}</span>{" "}{locale.text("brandingProductSuffix")}</h1>
            <p className="c3i-login-project-line">{locale.text("brandingForProjects")}</p>
            <p className="c3i-login-expansion">{locale.text("brandingExpansion")}</p>
            <p className="c3i-login-intro">{locale.text("brandingHeroIntro")}</p>
            <ul className="c3i-login-features">
              {platformFeatures.map((feature) => (
                <li key={feature.title}>
                  <span className="c3i-login-feature-icon"><OperationalIcon name={feature.icon} /></span>
                  <span><strong>{locale.text(feature.title)}</strong><span>{locale.text(feature.detail)}</span></span>
                </li>
              ))}
            </ul>
          </section>
          <div className="c3i-login-authentication">
            <Card
              actions={sessionPhase === null ? undefined : <StatusPill label={sessionPhase.label} tone={sessionPhase.tone} />}
              className="c3i-login-card"
              headingLevel={2}
              title={<span className="c3i-login-form-title">{locale.text("loginHeading")}</span>}
              variant="flat"
            >
              <p className="c3i-login-access">{locale.text("brandingLoginAccess")}</p>
              {summaryVisible ? (
                <ErrorState
                  className="login-error-summary"
                  description={
                    formError === "loginErrorSummaryTitle" ? undefined : locale.text(formError)
                  }
                  title={locale.text("loginErrorSummaryTitle")}
                />
              ) : null}
              <Form
                className="c3i-login-form"
                actions={
                  <Button className="c3i-login-submit" busy={submitting} busyLabel={locale.text("loginSubmitting")} type="submit" variant="primary">
                    <span className="c3i-login-submit-label"><OperationalIcon name="arrow-right" />{locale.text("loginSubmit")}</span>
                  </Button>
                }
                label={locale.text("loginHeading")}
                onSubmit={() => void submit()}
              >
                <Field controlId="login-identifier" error={identifierError === null ? undefined : locale.text(identifierError)} label={locale.text("loginIdentifierLabel")} required>
                  <div className="c3i-login-input-wrap"><OperationalIcon name="role" /><TextInput autoComplete="username" autoFocus className="c3i-login-input" name="loginIdentifier" onChange={(event) => setLoginIdentifier(event.target.value)} placeholder={locale.text("brandingLoginIdentifierPlaceholder")} value={loginIdentifier} /></div>
                </Field>
                <Field controlId="login-password" error={passwordError === null ? undefined : locale.text(passwordError)} label={locale.text("loginPasswordLabel")} required>
                  <div className="c3i-login-input-wrap"><OperationalIcon name="read-only" /><TextInput autoComplete="current-password" className="c3i-login-input" name="password" onChange={(event) => setPassword(event.target.value)} placeholder={locale.text("brandingLoginPasswordPlaceholder")} type="password" value={password} /></div>
                </Field>
              </Form>
              <p className="c3i-login-security"><OperationalIcon name="shield" />{locale.text("loginSecurityNote")}</p>
            </Card>
            <div className="c3i-login-connection">
              <span>{locale.text("brandingDeviceConnection")}</span>
              <StatusPill label={locale.text(status.online ? "connectivityOnline" : "connectivityOffline")} tone={status.online ? "neutral" : "warning"} />
            </div>
          </div>
        </div>
        <section aria-labelledby="c3i-research-title" className="c3i-research">
          <h2 className="c3i-research-title" id="c3i-research-title">{locale.text("brandingAboutProjects")}</h2>
          <div className="c3i-project-grid">
            {researchProjects.map((project) => (
              <Card className={`c3i-research-project c3i-research-project--${project.id}`} headingLevel={3} key={project.id} title={<span className="c3i-project-name">{project.name}</span>} variant="flat">
                <div aria-hidden="true" className="c3i-project-artwork">
                  <img alt="" height="1024" src={project.id === "synergise" ? responseArtwork : logisticsArtwork} width="1536" />
                </div>
                <div className="c3i-project-logo-position"><ProjectIdentity onDark={theme.theme === "dark-blue" || theme.theme === "high-contrast"} project={project} /></div>
                <p className="c3i-project-summary">{locale.text(project.id === "synergise" ? "brandingSynergiseSummary" : "brandingForesightSummary")}</p>
                <p className="c3i-project-grant"><span>{locale.text("brandingHorizonEurope")}</span><span>{locale.text("brandingGrantAgreement")} <strong>{project.grant}</strong></span></p>
                <a aria-label={`${locale.text("brandingProjectWebsite")} · ${project.name}`} className="c3i-project-information__link" href={project.url} referrerPolicy="no-referrer" rel="noopener noreferrer" target="_blank"><OperationalIcon name="open" />{locale.text("brandingProjectWebsite")}<OperationalIcon name="arrow-right" /></a>
              </Card>
            ))}
          </div>
        </section>
        <section aria-label={locale.text("brandingFundingInformation")} className="c3i-login-funding">
          <div className="c3i-login-funding__eu">
            <FundingAcknowledgement />
            <span>{researchProjects[0].name} · {locale.text("brandingGrantAgreement")} {researchProjects[0].grant}</span>
          </div>
          <div className="c3i-login-funding__copy">
            <p>{locale.text("brandingAttribution")}</p>
            <p>{locale.text("brandingForesightFunding")}</p>
          </div>
          <AstrialLogo className="c3i-login-vendor" title="Astrial" variant="full" />
        </section>
      </main>
      <footer className="c3i-login-footer">
        <div className="c3i-login-footer__actions">
          <p><strong>{locale.text("appName")}</strong><span>{locale.text("brandingAttribution")}</span></p>
          <ProjectInformation />
        </div>
      </footer>
    </div>
  );
});
